import React, { useState, useEffect } from 'react';
import { Clock, Edit, Plus, Trash, Eye, ChevronDown, ChevronUp } from 'react-feather';

const MedicineHistoryModal = ({ medicine, isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (isOpen && medicine) {
      setPage(1);
      fetchHistory(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, medicine?._id]);

  const fetchHistory = async (targetPage = page) => {
    if (!medicine?._id) return;
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('userToken');
      const API_BASE = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:5000`;
      const url = new URL(`${API_BASE}/api/history/medicine/${medicine._id}`);
      url.searchParams.set('page', targetPage);
      url.searchParams.set('limit', 10);
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch history');
      setHistory(data.data.history || []);
      setTotalPages(data.data.totalPages || 1);
      setPage(data.data.page || targetPage);
    } catch (err) {
      setError(err.message || 'Error fetching history');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return <Plus className="w-4 h-4 text-green-600" />;
      case 'updated': return <Edit className="w-4 h-4 text-blue-600" />;
      case 'deleted': return <Trash className="w-4 h-4 text-red-600" />;
      case 'availability_toggled': return <Eye className="w-4 h-4 text-orange-600" />;
      default: return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold">History for {medicine?.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading history...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No history found for this medicine.</div>
          ) : (
            <div className="space-y-3">
              {history.map((entry) => (
                <div key={entry._id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getActionIcon(entry.action)}
                      <div>
                        <div className="font-medium capitalize">{entry.action.replace('_', ' ')}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(entry.timestamp).toLocaleString()} · {entry.userId?.email || entry.userId?.name || 'Unknown user'}
                        </div>
                        {entry.changedFields?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {entry.changedFields.map((f) => (
                              <span key={f} className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">{f}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button onClick={() => toggleExpand(entry._id)} className="text-gray-500 hover:text-gray-700 flex items-center gap-1">
                      {expanded[entry._id] ? (<><ChevronUp className="w-4 h-4" /> Hide</>) : (<><ChevronDown className="w-4 h-4" /> Details</>)}
                    </button>
                  </div>
                  {expanded[entry._id] && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="bg-white border rounded p-2">
                        <div className="font-semibold mb-1">Previous</div>
                        <pre className="overflow-auto text-xs text-gray-700">{JSON.stringify(entry.previousData || {}, null, 2)}</pre>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <div className="font-semibold mb-1">New</div>
                        <pre className="overflow-auto text-xs text-gray-700">{JSON.stringify(entry.newData || {}, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="p-4 border-t flex items-center justify-between">
          <div className="text-sm text-gray-600">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchHistory(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1 rounded border bg-white text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => fetchHistory(Math.min(totalPages, page + 1))}
              disabled={page === totalPages || loading}
              className="px-3 py-1 rounded border bg-white text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicineHistoryModal;